/**
 * Teams API client — mirrors backend /api/v1/teams/* routes per TEAMS-SPEC.md.
 *
 * Response shape convention in AURA backend: `{ data: T, ...pagination? }`.
 * These helpers unwrap `.data.data` so callers get the raw model shape.
 */
import { api } from '@/utils/api';

// Legacy (DEPARTMENT_HEAD/MANAGER/EXECUTIVE) + the Apr-28-2026 refined
// hierarchy (TEAM_HEAD/BACKEND_STAFF/FIELD_EXECUTIVE). New team data uses the
// refined levels; both are kept so mixed rows type + count correctly.
export type TeamMemberLevel =
  | 'DEPARTMENT_HEAD'
  | 'MANAGER'
  | 'EXECUTIVE'
  | 'TEAM_HEAD'
  | 'BACKEND_STAFF'
  | 'FIELD_EXECUTIVE';

export interface TeamMemberUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string | null;
  departmentId?: string | null;
  department?: { id: string; name: string; code?: string } | null;
  designation?: string | null;
  isActive?: boolean;
}

export interface TeamMembership {
  id: string;
  teamId: string;
  userId: string;
  level: TeamMemberLevel;
  isPrimary: boolean;
  joinedAt: string;
  leftAt?: string | null;
  user: TeamMemberUser;
}

export interface Team {
  id: string;
  tenantId: string;
  departmentId: string;
  department?: { id: string; name: string; code?: string } | null;
  name: string;
  code?: string | null;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  memberships?: TeamMembership[];
  /** Optional denormalised counts the backend may return for list views. */
  counts?: {
    managers: number;
    executives: number;
    total: number;
  };
  /** Convenience: the current DEPARTMENT_HEAD membership (if loaded). */
  departmentHead?: TeamMembership | null;
}

export interface ListTeamsParams {
  departmentId?: string;
  isActive?: boolean;
  q?: string;
}

export interface CreateTeamBody {
  departmentId: string;
  name: string;
  code?: string;
  description?: string;
}

export interface UpdateTeamBody {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface AddMemberBody {
  userId: string;
  level: 'MANAGER' | 'EXECUTIVE';
}

export interface UpdateMemberBody {
  level: 'MANAGER' | 'EXECUTIVE';
}

// ── List / Detail ───────────────────────────────────────────────────────────

export async function listTeams(params: ListTeamsParams = {}): Promise<Team[]> {
  const query: Record<string, string> = {};
  if (params.departmentId) query.departmentId = params.departmentId;
  if (typeof params.isActive === 'boolean') query.isActive = String(params.isActive);
  if (params.q) query.q = params.q;
  const res = await api.get('/teams', { params: query });
  return (res.data?.data ?? res.data ?? []) as Team[];
}

export async function getTeam(id: string): Promise<Team> {
  const res = await api.get(`/teams/${id}`);
  return (res.data?.data ?? res.data) as Team;
}

// ── Create / Update / Delete ────────────────────────────────────────────────

export async function createTeam(body: CreateTeamBody): Promise<Team> {
  const res = await api.post('/teams', body);
  return (res.data?.data ?? res.data) as Team;
}

export async function updateTeam(id: string, body: UpdateTeamBody): Promise<Team> {
  const res = await api.patch(`/teams/${id}`, body);
  return (res.data?.data ?? res.data) as Team;
}

export async function deleteTeam(id: string): Promise<void> {
  await api.delete(`/teams/${id}`);
}

// ── Membership ──────────────────────────────────────────────────────────────

export async function listMembers(teamId: string): Promise<TeamMembership[]> {
  const res = await api.get(`/teams/${teamId}/members`);
  return (res.data?.data ?? res.data ?? []) as TeamMembership[];
}

export async function addMember(
  teamId: string,
  body: AddMemberBody,
): Promise<TeamMembership> {
  const res = await api.post(`/teams/${teamId}/members`, body);
  return (res.data?.data ?? res.data) as TeamMembership;
}

export async function updateMember(
  teamId: string,
  membershipId: string,
  body: UpdateMemberBody,
): Promise<TeamMembership> {
  const res = await api.patch(`/teams/${teamId}/members/${membershipId}`, body);
  return (res.data?.data ?? res.data) as TeamMembership;
}

export async function removeMember(
  teamId: string,
  membershipId: string,
): Promise<void> {
  await api.delete(`/teams/${teamId}/members/${membershipId}`);
}
